/**
 * Realtime call-detail channels / events.
 *
 * Backend contract (public Laravel Echo channel):
 *   call-detail.{callId}
 *     call.response.created
 *     call.response.updated
 *     call.response.deleted
 *     call.response.like
 *     call.response.like.removed
 *
 * Override with env if the backend ever changes the template:
 *   REACT_APP_PUSHER_CALL_DETAIL_CHANNEL=call-detail.{callId}
 */

import {
  eventNameAliases,
  unwrapRealtimePayload,
} from "./messagesRealtime"

export const CALL_RESPONSE_CREATED_EVENT = "call.response.created"
export const CALL_RESPONSE_UPDATED_EVENT = "call.response.updated"
export const CALL_RESPONSE_DELETED_EVENT = "call.response.deleted"
export const CALL_RESPONSE_LIKE_EVENT = "call.response.like"
export const CALL_RESPONSE_LIKE_REMOVED_EVENT = "call.response.like.removed"

export const CALL_RESPONSE_CREATED_EVENTS = eventNameAliases(
  CALL_RESPONSE_CREATED_EVENT,
)
export const CALL_RESPONSE_UPDATED_EVENTS = eventNameAliases(
  CALL_RESPONSE_UPDATED_EVENT,
)
export const CALL_RESPONSE_DELETED_EVENTS = eventNameAliases(
  CALL_RESPONSE_DELETED_EVENT,
)
export const CALL_RESPONSE_LIKE_EVENTS = eventNameAliases(
  CALL_RESPONSE_LIKE_EVENT,
)
export const CALL_RESPONSE_LIKE_REMOVED_EVENTS = eventNameAliases(
  CALL_RESPONSE_LIKE_REMOVED_EVENT,
)

export const getCallDetailChannelName = callId => {
  if (callId == null || callId === "") return ""
  const template = process.env.REACT_APP_PUSHER_CALL_DETAIL_CHANNEL
  const base = template
    ? String(template)
        .replace(/\{callId\}/g, String(callId))
        .replace(/\{id\}/g, String(callId))
    : `call-detail.${callId}`
  return String(base || "").replace(/^(private-|presence-)/, "")
}

const asObject = value =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null

const payloadCallId = data => data?.call_id ?? data?.callId ?? null

export const matchesCallRealtime = (callId, payloadCall) => {
  if (payloadCall == null || payloadCall === "") return true
  return String(payloadCall) === String(callId)
}

const normalizeRealtimeResponse = (response, fallbackUser = null) => {
  const raw = asObject(response)
  if (!raw?.id && raw?.id !== 0) return null
  const sender = asObject(raw.sender) || asObject(raw.user) || asObject(fallbackUser)
  return {
    ...raw,
    user: asObject(raw.user) || sender,
    user_id: raw.user_id || sender?.id || null,
    parent_comment_id: raw.parent_comment_id || raw.parent_id || null,
  }
}

export const parseCallResponsePayload = payload => {
  const data = unwrapRealtimePayload(payload)
  if (!data || typeof data !== "object") return null

  const nested = asObject(data.response) || asObject(data.comment)
  const response = normalizeRealtimeResponse(
    nested || data,
    data.sender || data.user,
  )
  if (!response) return null

  return {
    callId: payloadCallId(data),
    response,
  }
}

export const parseCallResponseDeletedPayload = payload => {
  const data = unwrapRealtimePayload(payload)
  if (!data || typeof data !== "object") return null

  const nested = asObject(data.response) || asObject(data.comment)
  const responseId =
    nested?.id ??
    data.response_id ??
    data.comment_id ??
    data.id ??
    null
  if (responseId == null || responseId === "") return null

  return {
    callId: payloadCallId(data),
    responseId,
  }
}

export const parseCallResponseLikePayload = payload => {
  const data = unwrapRealtimePayload(payload)
  if (!data || typeof data !== "object") return null

  const like = asObject(data.like) || {}
  const comment = asObject(data.comment) || {}
  const commentId = comment.id ?? like.comment_id ?? data.comment_id ?? null
  if (commentId == null || commentId === "") return null

  const likesCount = Number(comment.likes_count)
  const sender = asObject(like.sender) || asObject(like.user)

  return {
    callId: payloadCallId(data),
    commentId,
    likesCount: Number.isFinite(likesCount) ? Math.max(0, likesCount) : null,
    userId: like.user_id || sender?.id || null,
    like: {
      ...like,
      user_id: like.user_id || sender?.id || null,
      sender,
    },
  }
}
